/**
 * @author        patrick@zumper.com (Patrick Giddings)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   selectMaskedValue util
 */

import { memoizeResultsFunc } from '@zcode/memoizers'

const matchOperatorsRegex = /[|\\{}()[\]^$+*?.-]/g

const escapeStringRegExp = (str: string) => {
  return str.replace(matchOperatorsRegex, '\\$&')
}

// NOTE: format chars should always be single chars, although TS does not enforce
export interface FormatChars {
  readonly [char: string & { length: 1 }]: string
}

interface LooseFormatChars {
  readonly [char: string]: string
}

const DEFAULT_FORMAT_CHARS = { 9: '[0-9]', a: '[A-Za-z]', '*': '[A-Za-z0-9]' } as const

const convertFormatChars = memoizeResultsFunc((mask: string, formatChars: LooseFormatChars) => {
  const re = new RegExp(`(?:\\\\)?[^${Object.keys(formatChars).join('')}]`, 'g')
  let converted = mask.replace(re, '(?:$&?')
  Object.entries<string>(formatChars).forEach(([char, format]) => {
    const charRe = new RegExp(escapeStringRegExp(char), 'g')
    converted = converted.replace(charRe, `(?:(${format})?`)
  })
  const parenCount = converted.match(/\(\?:/g)?.length || 0
  converted += ')'.repeat(parenCount)
  return converted
})

const selectFormatCharRegExp = memoizeResultsFunc((formatChars: FormatChars) => {
  return new RegExp(Object.keys(formatChars).map(escapeStringRegExp).join('|'), 'g')
})

const selectMaskRegExp = memoizeResultsFunc((mask: string, formatChars: LooseFormatChars) => {
  return new RegExp(convertFormatChars(escapeStringRegExp(mask), formatChars))
})

export const selectMaskedValue = memoizeResultsFunc(
  (value: string | undefined, mask: string, formatChars: FormatChars = DEFAULT_FORMAT_CHARS) => {
    let newValue = ''

    if (!value) {
      return newValue
    }

    const re = selectMaskRegExp(mask, formatChars as LooseFormatChars)
    const matches = value.match(re)

    if (matches?.[1]) {
      const chars = matches.slice(1).filter((v) => v != null)

      const formatCharRE = selectFormatCharRegExp(formatChars)
      newValue = chars.reduce(
        (v, char) => (char ? v.replace(/@@m/, char) : v),
        mask.replace(formatCharRE, '@@m')
      )

      if (newValue.includes(`@@m`)) {
        const firstPlaceholderIdx = newValue.indexOf('@@m')
        newValue = newValue.slice(0, firstPlaceholderIdx)
      }

      if (chars.length < matches.length - 1) {
        for (
          let index = newValue.length - 1;
          index && newValue[index] !== chars[chars.length - 1];
          index--
        ) {
          newValue = newValue.substring(0, newValue.length - 1)
        }
      }
    }

    // Don't block users from typing the static chars in the format string
    const nextChar = mask.charAt(newValue.length)
    if (
      nextChar &&
      value.charAt(newValue.length) === nextChar &&
      !Object.keys(formatChars).includes(nextChar)
    ) {
      newValue += nextChar
    }

    return newValue
  }
)
